import { Logger } from '@nice-ccf/core-sdk';
import { CXoneAuth } from '@nice-ccf/agent-sdk';
import { AuthSettings, StartSessionRequest } from '@nice-ccf/common-sdk';
import { gaLoginActions } from '../ccf-ga-login/gaLoginSlice';
import { voicePreferenceActions } from '../ccf-voice-preference/agentSessionSlice';

/**
 * Middleware for CCF-UI and CCF-SDK integration to handle asynchronous requests
 * @param action - action method dispatched from the component
 * @example - Handles asynchronous tasks, modified action payload and sends it to the slice to update the state
 */
export const Middleware = (store: any) => (next: any) => (action: any) => {
  const authManager: CXoneAuth = CXoneAuth.instance;
  let cxoneAuthWindow: any;
  let isWebRTCEnabled = false;
  const errorMessage = document.getElementById('errorMessage') as HTMLElement;

  /**
   * Check product ID for webrtc in business unit features
   * @example - sets isWebRTCEnabled to true if product ID 110 is available
   */
  const checkFeatures = (businessUnit: any) => {
    const features = businessUnit.features;
    features.forEach((feature: any) => {
      if (feature.productId === 110) {
        isWebRTCEnabled = true;
      }
    });
  };
  /**
   * check permissions key for softphone
   * @example - sets isWebRTCEnabled to true if agentsfsoftphone key is available in permissions
   */
  const checkPermissions = (permissions: any) => {
    permissions.forEach((permission: any) => {
      if (permission.key.toLowerCase() === 'agentsfsoftphone') {
        isWebRTCEnabled = true;
      }
    });
  };
  /**
   * check audiocodes availability in agent settings
   * @example - sets isWebRTCEnabled to true if audiocodes is available in agentSettings,
   * adds softphone option to action payload and dispatches action to slice to update the state
   */
  const checkAgentSettings = (agentSettings: any) => {
    if (
      agentSettings &&
      agentSettings.webRTCType.toLowerCase() === 'audiocodes'
    ) {
      action.payload = true;
      next(action);
    }
  };

  if (gaLoginActions.login.match(action)) {
    const logger: Logger = new Logger(
      action.payload.consumerAppName,
      'ccf-ga-login'
    );
    const authSettings: AuthSettings = action.payload.authSettings;
    authManager.init(authSettings);
    authManager
      .getAuthorizeUrl(
        action.payload.authMode,
        action.payload.codeChallengeMethod
      )
      .then(
        (authUrl: string) => {
          if (action.payload.authMode === 'page') {
            window.location.href = authUrl;
          } else if (action.payload.authMode === 'popup') {
            const width = window.screen.availWidth * 0.8;
            const height = window.screen.availWidth * 0.8;
            const left = 50;
            const top = 50;
            const popupOptions =
              'width=' +
              width +
              ',height=' +
              height +
              ',scrollbars=yes,toolbar=no,left=' +
              left +
              ',top=' +
              top;
            cxoneAuthWindow = window.open(authUrl, 'authWindow', popupOptions);

            window.addEventListener(
              'message',
              (event) => {
                if (event.data['messageType'] === 'authcode') {
                  const code =
                    new URLSearchParams(event.data['message']).get('code') ||
                    '';
                  authManager
                    .getAccessToken({
                      clientId: action.payload.authSettings.clientId,
                      code: code,
                    })
                    .then((response) => {
                      logger.info(
                        'getAccessToken',
                        'Authenticated successfully'
                      );
                      action.payload.loginConsumerCallback(
                        'Authenticated successfully'
                      );
                      action.payload = true;
                      next(action);
                    });
                  cxoneAuthWindow?.close();
                }
              },
              false
            );
          }
        },
        (err) => {
          logger.error('loginClickHandler', JSON.stringify(err));
        }
      );
  } else if (voicePreferenceActions.isWebRTCEnabled.match(action)) {
    const logger: Logger = new Logger(
      action.payload.consumerAppName,
      'ccf-voice-preference.isWebRTCEnabled'
    );

    const businessUnitFromStorage = localStorage.getItem('business_unit');
    if (!businessUnitFromStorage) {
      authManager.getBusinessUnit().then((businessUnit: any) => {
        checkFeatures(businessUnit);
        if (isWebRTCEnabled) {
          authManager.getPermissions().then((permissions: any) => {
            logger.info('getAgentSettings', JSON.stringify(permissions));
            isWebRTCEnabled = false;
            checkPermissions(permissions);
            if (isWebRTCEnabled) {
              authManager.getAgentSettings().then((agentSettings: any) => {
                logger.info('getClientData', JSON.stringify(agentSettings));
                checkAgentSettings(agentSettings);
              });
            }
          });
        }
      });
    } else {
      const businessUnit = JSON.parse(
        localStorage.getItem('business_unit') || ''
      );
      const permissions = JSON.parse(localStorage.getItem('permissions') || '');
      const agentSettings = JSON.parse(
        localStorage.getItem('agent_settings') || ''
      );

      checkFeatures(businessUnit);
      if (isWebRTCEnabled) {
        logger.info('getAgentSettings', JSON.stringify(permissions));
        isWebRTCEnabled = false;
        checkPermissions(permissions);
      }
      if (isWebRTCEnabled) {
        logger.info('getClientData', JSON.stringify(agentSettings));
        checkAgentSettings(agentSettings);
      }
    }
  } else if (
    voicePreferenceActions.checkVoicePrefRadioSelection.match(action)
  ) {
    next(action);
  } else if (voicePreferenceActions.setVoicePrefInputText.match(action)) {
    next(action);
  } else if (voicePreferenceActions.setCheckBoxSelection.match(action)) {
    next(action);
  } else if (voicePreferenceActions.setAgentSession.match(action)) {
    const logger: Logger = new Logger(
      action.payload.consumerAppName,
      'ccf-voice-preference.setAgentSession'
    );
    const startSessionObj: StartSessionRequest = {
      stationId:
        action.payload.selectedVoicePref === 'stationId'
          ? action.payload.voiceInputVal
          : '',
      stationPhoneNumber:
        action.payload.selectedVoicePref === 'phone'
          ? action.payload.voiceInputVal
          : action.payload.selectedVoicePref === 'softPhone' ? 'WebRTC' : '',
    };
    authManager.startSession(startSessionObj).then(
      (agentSessionResponse) => {
        logger.info('startSession', JSON.stringify(agentSessionResponse));
        action.payload.sessionConsumerCallback(
          'session established',
          action.payload.selectedVoicePref
        );
        next(action);
      },
      (error) => {
        if (error.status === 409) {
          authManager.joinSession().then(
            (joinSessionResponse) => {
              logger.info('joinSession', JSON.stringify(joinSessionResponse));
              action.payload.sessionConsumerCallback(
                'session established',
                action.payload.selectedVoicePref
              );
              next(action);
            },
            (error) => {
              logger.error('joinSession', JSON.stringify(error));
            }
          );
        } else if (error.status === 400) {
          logger.error('joinSession', JSON.stringify(error));
          errorMessage.innerHTML = error.statusText;
          errorMessage.style.display = 'block';
        }
      }
    );
  }
};

export default Middleware;
